// src/pages/healthcare/results/testOrderComponents/ReportTab.tsx
import React, { useState, useEffect } from "react";
import { Button, Alert, Modal, Space, Progress, Divider } from "antd";
import { FilePdfOutlined, FileOutlined, AudioOutlined, DownloadOutlined, DeleteOutlined, NotificationOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { getSessionUser } from "../../../../utils/auth/sessionUser";

import SectionCard from "../../../../components/shared/SectionCard";
import DataTable from "../../../../components/shared/DataTable";

// Types
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { ReportFile } from "../../../../types/file/FileTypes";
import { Company } from "../../../../types/admin/CompanyTypes";

// API
import { deleteReport, downloadReport, listPatientReports, updateClinicalOnlyReport } from "../../../../api/file/fileServiceApi";
import {
	generateMethylationReport,
	generateGeneticReport,
	getSampleVariants,
	SampleVariants,
	createAudiobookReport,
	generateActionReport,
} from "../../../../api/labiq/GenerateReportsApi";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";

// Utils/hooks
import { useNotifications } from "../../../../context/NotificationContext";
import { extractValidationErrors } from "../../../../utils/apiError";
import { isFormDone } from "../../../../utils/forms/formStatus";
import { sendCommunication } from "../../../../api/communications/communicationApi";
import { SendCommunicationRequest } from "../../../../types/communications/CommunicationTypes";

import { AssignedForm, FormResponse, getAssignedForms, getFormResults } from "../../../../api/formApi";
import { fetchMedicalHistories, fetchPatient } from "../../../../api/healthcare/PatientApi";
import { labiqUserId } from "../../../../utils/auth/labiqUserId";

interface ReportTabProps {
	patient: Patient;
	clientSampleId: string;
}

const ReportTab: React.FC<ReportTabProps> = ({ patient, clientSampleId }) => {
	const user = getSessionUser();
	const [reports, setReports] = useState<ReportFile[]>([]);
	const [sampleVariants, setSampleVariants] = useState<SampleVariants | null>(null);
	const [loading, setLoading] = useState<{
		generating: { methylation: boolean; genetic: boolean; actionPlan: boolean; supplement: boolean; pre: boolean };
	}>({
		generating: { methylation: false, genetic: false, actionPlan: false, supplement: false, pre: false },
	});
	const [sendingNotification, setSendingNotification] = useState<boolean>(false);
	const [company, setCompany] = useState<Company>(null);

	const [downloadProgress, setDownloadProgress] = useState<{
		[fileId: string]: { progress: number; downloading: boolean; fileName?: string };
	}>({});
	const [error, setError] = useState<string | null>(null);
	const { showError, showSuccess } = useNotifications();



	useEffect(() => {
		async function loadCompany() {
			try {
				const data = await fetchClinicCompany();
				setCompany(data);
			} catch (error) {
				console.error("Failed to load company data:", error);
			}
		}

		loadCompany();
		loadData();
		loadSampleVariants();
	}, []);

	const loadData = async () => {
		try {
			const data = await listPatientReports(patient.id, clientSampleId);
			setReports(data);
		} catch (error) {
			console.error("Failed to load reports:", error);
		}
	};

	const loadSampleVariants = async () => {
		try {
			const variants = await getSampleVariants(clientSampleId);
			setSampleVariants(variants);
		} catch (error) {
			console.error("Failed to load sample variants:", error);
			setError("Failed to load genetic data for this sample");
		}
	};

	const handleGenerateMethylationReport = async () => {
		setLoading((prev) => ({ ...prev, generating: { ...prev.generating, methylation: true } }));
		try {
			await generateMethylationReport(patient, clientSampleId);
			showSuccess("Methylation report generated successfully");
			loadData();
		} catch (error: any) {
			console.error("Failed to generate methylation report:", error);
			showError(extractValidationErrors(error).join(' '));
		} finally {
			setLoading((prev) => ({ ...prev, generating: { ...prev.generating, methylation: false } }));
		}
	};

	const handleGenerateGeneticReport = async () => {
		setLoading((prev) => ({ ...prev, generating: { ...prev.generating, genetic: true } }));
		try {
			let patientForms = [];
			let assignedforms: AssignedForm[] = await getAssignedForms(patient.id);
			for (const form of assignedforms) {
				// A `Reviewed` form is `Completed` plus a practitioner having read it. Testing
				// only for `Completed` dropped a patient's answers out of the report the moment
				// somebody reviewed them — the report still generated, just thinner.
				if (isFormDone(form.status)) {
					const formResults: FormResponse = await getFormResults(form.id);
					if (formResults) {
						patientForms.push(formResults);
					}
				}
			}
			const medicalInfo = await fetchMedicalHistories(patient.id);
			const { data } = await fetchPatient(patient.id as unknown as string);

			const patientData: Patient = {
				...data,
				forms: patientForms,
				medical_history: medicalInfo,
			};

			let response = await generateGeneticReport(patientData, clientSampleId);
			showSuccess(response.message);
			loadData();
		} catch (error: any) {
			console.error("Failed to generate genetic report:", error);
			showError(extractValidationErrors(error).join(' '));
		} finally {
			setLoading((prev) => ({ ...prev, generating: { ...prev.generating, genetic: false } }));
		}
	};

	const handleGenerateActionReport = async () => {
		setLoading((prev) => ({ ...prev, generating: { ...prev.generating, actionPlan: true } }));
		try {
			let patientForms = [];
			let assignedforms: AssignedForm[] = await getAssignedForms(patient.id);
			for (const form of assignedforms) {
				// Same predicate as the genetic report above — see the note there.
				if (isFormDone(form.status)) {
					const formResults: FormResponse = await getFormResults(form.id);
					if (formResults) {
						patientForms.push(formResults);
					}
				}
			}
			const medicalInfo = await fetchMedicalHistories(patient.id);
			const { data } = await fetchPatient(patient.id as unknown as string);

			const patientData: Patient = {
				...data,
				forms: patientForms,
				medical_history: medicalInfo,
			};

			await generateActionReport(patientData, clientSampleId);
			showSuccess("Action plan report generated successfully");
			loadData();
		} catch (error: any) {
			console.error("Failed to generate action report:", error);
			showError(extractValidationErrors(error).join(' '));
		} finally {
			setLoading((prev) => ({ ...prev, generating: { ...prev.generating, actionPlan: false } }));
		}
	};

	const handleDownload = async (rowData: ReportFile) => {
		try {
			setDownloadProgress((prev) => ({
				...prev,
				[rowData.id]: { progress: 0, downloading: true, fileName: rowData.file_name },
			}));

			const onProgress = (progressEvent: any) => {
				if (progressEvent.lengthComputable) {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					setDownloadProgress((prev) => ({
						...prev,
						[rowData.id]: {
							progress: percentCompleted,
							downloading: true,
							fileName: rowData.file_name,
						},
					}));
				}
			};

			await downloadReport(patient.id, rowData.id, onProgress);
			showSuccess(`${rowData.file_name} downloaded successfully`);

			setTimeout(() => {
				setDownloadProgress((prev) => {
					const updated = { ...prev };
					delete updated[rowData.id];
					return updated;
				});
			}, 2000);
		} catch (error) {
			console.error("Download failed:", error);
			showError(`Failed to download ${rowData.file_name}`);

			setDownloadProgress((prev) => {
				const updated = { ...prev };
				delete updated[rowData.id];
				return updated;
			});
		}
	};

	// M16 (ClickUp 86d3r82eh) — WHICH REPORT an audiobook would be made from, or null for
	// "no audiobook from this file".
	//
	// *** THIS REPLACED `isMethylationReport()`, WHICH WAS A BOOLEAN. *** A boolean could
	// only ever answer "show the button", and the button now has to say WHICH plan to use:
	// the chapter plans are agent-side data keyed by exactly these strings.
	//
	// ⚠️ **FILENAME MATCHING IS FRAGILE AND IS INHERITED, NOT INTRODUCED.** The old gate was
	// `fileName.toLowerCase().includes("methylation")` against
	// `PdfReportService::generateMethylationReport`'s `"Methylation Report - {name}.pdf"`.
	// Renaming a report in `PdfReportService` silently removes this button, and always could.
	// The right fix is a `report_type` field on the file record; it is its own ticket and is
	// deliberately NOT built here.
	//
	// ⚠️ **RETURNING null FOR ANYTHING ELSE IS THE POINT, NOT AN OVERSIGHT.** It is what
	// keeps `"Diet info - ..."` (`generateActionPlanReport`) out — that report has no chapter
	// plan, and offering the button would queue a job the agent refuses with a 404.
	const resolveAudiobookSourceType = (fileName: string): string | null => {
		const name = fileName.toLowerCase();

		// ⚠️ METHYLATION IS TESTED FIRST AND THAT ORDER IS LOAD-BEARING. The methylation
		// report's own body is about genetics, and a future filename carrying both words must
		// keep resolving the way it always has — this branch is the pre-M16 behaviour exactly.
		if (name.includes("methylation")) return "labiq_methylation_report";
		if (name.includes("genetic")) return "labiq_genetic_report";

		return null;
	};

	const handleAudiobookCreation = async (rowData: ReportFile, sourceType: string) => {
		const labiqPatientId = labiqUserId(patient);
		if (labiqPatientId === undefined) {
			console.error("labiq.audiobook.legacy_user_id_missing", { patientId: patient.id });
			showError("Failed to create audiobook. Please try again.");
			return;
		}
		try {
			let response = await createAudiobookReport(labiqPatientId, rowData.id, clientSampleId, sourceType);
			if (response.status === "queued") {
				showSuccess(response.message || "Audiobook generation has been queued successfully");
			} else {
				showError("Failed to create audiobook. Please try again.");
			}
		} catch (error) {
			console.error("Audiobook creation failed:", error);
			showError(extractValidationErrors(error).join(' '));
		}
	};

	const handleDelete = async (rowData: ReportFile) => {
		Modal.confirm({
			title: "Delete Report",
			content: "Are you sure you want to delete this report?",
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteReport(rowData.id, patient.id);
					showSuccess("Report deleted successfully");
					loadData();
				} catch (error) {
					console.error("Delete failed:", error);
					showError("Failed to delete report");
				}
			},
		});
	};

	const handleSendNotification = async (reportType: string) => {
		setSendingNotification(true);
		try {
			const payload: SendCommunicationRequest = {
				recipient_user_id: patient.id,
				category: reportType,
				channels: ["email", "sms"],
				company_id: company.id,
				user_id: user.id,
				data: {
					...patient,
				},
			};
			await sendCommunication(payload);
			showSuccess("Notification has been sent to the patient");
		} catch (error) {
			console.error("Failed to send notification:", error);
			showError("Failed to send notification. Please try again.");
		} finally {
			setSendingNotification(false);
		}
	};

	const handleToggleClinicalOnly = async (fileid: string, clinicalOnly: boolean) => {
		try {
			await updateClinicalOnlyReport(fileid, !clinicalOnly);
			loadData();
		} catch (error: unknown) {
			showError("Failed to update report settings. Please try again.");
		}
	};

	const columns: ColumnsType<ReportFile> = [
		{
			title: "Report Name",
			key: "file_name",
			render: (_, record) => {
				const downloadInfo = downloadProgress[record.id];
				return (
					<div>
						<div>{record.file_name}</div>
						{downloadInfo?.downloading && <Progress percent={downloadInfo.progress} size="small" status="active" style={{ marginTop: 8 }} />}
					</div>
				);
			},
		},
		{
			title: "Date Created",
			dataIndex: "created_at",
			key: "created_at",
			render: (date) => new Date(date).toLocaleDateString(),
			sorter: true,
		},
		{
			title: "Clinical Only",
			dataIndex: "clinical_only",
			key: "clinical_only",
			render: (clinical_only: boolean, record: ReportFile) => (
				<Space>
					{clinical_only ? (
						<Button data-testid="report-clinical-toggle" type="text" icon={<CheckCircleOutlined style={{ color: "var(--mg-success)", fontSize: 16 }} />} onClick={() => handleToggleClinicalOnly(record.id, clinical_only)} />
					) : (
						<Button data-testid="report-clinical-toggle" type="text" danger icon={<CloseCircleOutlined style={{ color: "var(--mg-danger)", fontSize: 16 }} />} onClick={() => handleToggleClinicalOnly(record.id, clinical_only)} />
					)}
				</Space>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 150,
			align: "center",
			render: (_, record) => {
				const audiobookSourceType = resolveAudiobookSourceType(record.file_name);
				const downloadInfo = downloadProgress[record.id];
				const isDownloading = downloadInfo?.downloading || false;

				return (
					<Space>
						<Button
							data-testid="report-download-btn"
							type="text"
							icon={<DownloadOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDownload(record);
							}}
							loading={isDownloading}
							title={isDownloading ? `Downloading... ${downloadInfo?.progress || 0}%` : "Download"}
						/>
						{audiobookSourceType !== null && (
							<Button
								data-testid="report-audiobook-btn"
								type="text"
								icon={<AudioOutlined />}
								onClick={(e) => {
									e.stopPropagation();
									handleAudiobookCreation(record, audiobookSourceType);
								}}
								title="Create Audiobook"
							/>
						)}
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDelete(record);
							}}
							title="Delete"
						/>
					</Space>
				);
			},
		},
	];

	return (
		<div>
			{error && <Alert message={error} type="error" closable onClose={() => setError(null)} />}

			{/* Report Generation */}
			<SectionCard title="Generate Reports" data-testid="report-section">
				<Space wrap style={{paddingBottom: '5px'}}>
					<Button type="primary" icon={<FilePdfOutlined />} onClick={handleGenerateMethylationReport} loading={loading.generating.methylation} disabled={!sampleVariants?.methylation_ready}>
						Methylation
					</Button>
					<Button data-testid="report-generate-genetic" icon={<FileOutlined />} onClick={handleGenerateGeneticReport} loading={loading.generating.genetic}>
						Genetic
					</Button>
					<Button icon={<FileOutlined />} onClick={handleGenerateActionReport} loading={loading.generating.actionPlan}>
						Dietary
					</Button>

					<Divider type="vertical" className="mr-2" />
					<Button icon={<NotificationOutlined />} onClick={() => handleSendNotification("result_emailed_notification")} loading={sendingNotification}>
						New Resource Notification
					</Button>
					<Button icon={<NotificationOutlined />} onClick={() => handleSendNotification("methylation_report_ready_notification")} loading={sendingNotification}>
						Methylation Only Patient Notification
					</Button>
				</Space>

<DataTable bare columns={columns} dataSource={reports} rowKey="id" pagination={{ pageSize: 10 }} scrollX={800} />
		</SectionCard>


		</div>
	);
};

export default ReportTab;
